<template>
  <BaseCard class="mandala-section">
    <div class="d-flex justify-content-between align-items-center">
      <MonthButton
        v-model="selectedYearMonthDate"
        format="yyyy年M月 月間目標"
        @click="clickMonthButton" />
      <div
        v-if="mandalaGoalTemplate"
        class="mandala-section__template-name">
        [{{ mandalaGoalTemplate.name | truncate }}]
      </div>
    </div>
    <div v-if="isLoading" />
    <div
      v-else-if="!latestMandalaEvaluation"
      class="mandala-section__empty-message pt-2">
      上のボタンから目標設定を行いましょう
    </div>
    <CenteredBox
      v-else
      :offset-y="20">
      <RadarChart
        :labels="labels"
        display-legend
        :datasets="chartData"
        :padding="{ top: 40 }"
        :width="chartWidth"
        @click="onClickChart" />
      <template #child>
        <MandalaSectionDeadlineMessage
          v-if="isCurrentYearMonth && !isLastMonthMandalaEvaluationSubmitted && mandalaGoalTemplate && mandalaGoalTemplate.monthlyDeadlineDay && hasPreviousMandalaEvaluation"
          class="mandala-section__deadline-message"
          :user-id="memberId"
          :last-year-month="latest3YearMonths[1]"
          :mandala-goal-template="mandalaGoalTemplate" />
      </template>
    </CenteredBox>
    <MandalaEvaluationModal
      v-if="latestMandalaEvaluation && mandalaGoalTemplate"
      v-model="showMandalaEvaluationEntriesModal"
      :mandala-evaluations="[latestMandalaEvaluation, secondMandalaEvaluation]"
      :mandala-goal-template="mandalaGoalTemplate"
      :is-mine="isMine"
      :is-any-manager="isAnyManager" />
  </BaseCard>
</template>

<script>
import BaseCard from '@/components/parts/BaseCard.vue'
import RadarChart from '@/components/charts/RadarChart.vue'
import MonthButton from '@/components/parts/MonthButton.vue'
import MandalaEvaluationModal from '@/components/view/MandalaEvaluationModal.vue'
import MandalaSectionDeadlineMessage from '@/components/view/MandalaSectionDeadlineMessage.vue'
import CenteredBox from '@/components/parts/CenteredBox.vue'
import { format, startOfMonth, addMonths } from 'date-fns'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    BaseCard,
    RadarChart,
    MonthButton,
    MandalaEvaluationModal,
    MandalaSectionDeadlineMessage,
    CenteredBox
  },
  props: {
    memberId: {
      type: String,
      required: true
    },
    initialYearMonthDate: {
      type: Date,
      required: true
    }
  },
  data() {
    return {
      selectedYearMonthDate: new Date(),
      previousMandalaEvaluation: null,
      mandalaEvaluations: [],
      showMandalaEvaluationEntriesModal: false,
      mandalaGoalTemplate: null,
      isLoading: false
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isAnyManager']),
    isMine() {
      return this.$route.params.id === this.user.id
    },
    today() {
      return new Date() 
    },
    currentYearMonth() {
      return format(startOfMonth(this.today), 'yyyy-MM-dd')
    },
    isCurrentYearMonth() {
      return this.selectedYearMonth === this.currentYearMonth
    },
    // 現在時刻からみて先月の自己評価
    lastMonthMandalaEvaluation() {
      const lastMonth = format(startOfMonth(addMonths(this.today, -1)), 'yyyy-MM-dd')
      return this.mandalaEvaluationByYearMonth[lastMonth]
    },
    isLastMonthMandalaEvaluationSubmitted() {
      if (!this.lastMonthMandalaEvaluation) {
        return false
      }
      return this.lastMonthMandalaEvaluation.status === 'submitted'
    },
    // 取得した中で一番新しい自己評価
    latestMandalaEvaluation() {
      return this.mandalaEvaluations[0] || null
    },
    // 取得した中で２番目に新しい自己評価
    secondMandalaEvaluation() {
      return this.mandalaEvaluations[1] || null
    },
    hasLatestMandalaEvaluation() {
      return this.latestMandalaEvaluation !== null
    },
    mandalaEvaluationByYearMonth() {
      return this.mandalaEvaluations.reduce((prev, curr) => {
        return { ...prev, [curr.yearMonth]: curr }
      }, {})
    },
    colors() {
      return [
        '#E91E63',
        '#F48FB1',
        '#BDBDBD',
      ]
    },
    labels() {
      if (!this.latestMandalaEvaluation) {
        // 空の場合でもグラフは表示しておきたいので、空配列を返す
        return ['', '', '', '', '', '', '', '']
      }
      return this.latestMandalaEvaluation.mandalaGoalSelfEvaluationEntries.map(entry => entry.name ? entry.name.slice(0, 4) : '')
    },
    chartData() {
      return this.latest3YearMonths.reduce((prev, curr, index) => {
        const mandalaEvaluation = this.mandalaEvaluationByYearMonth[curr]
        if (!mandalaEvaluation) {
          return prev
        }
        return [...prev, this.generateChartDataFromMandalaEvaluation(mandalaEvaluation, this.colors[index])]
      }, [])
    },
    selectedYearMonth() {
      return format(startOfMonth(this.selectedYearMonthDate), 'yyyy-MM-dd')
    },
    latest3YearMonths() {
      return [
        startOfMonth(this.selectedYearMonthDate),
        startOfMonth(addMonths(this.selectedYearMonthDate, -1)),
        startOfMonth(addMonths(this.selectedYearMonthDate, -2)),
      ].map(date => format(date, 'yyyy-MM-dd'))
    },
    latest3YearMonthStr() {
      return this.latest3YearMonths.join(',')
    },
    hasPreviousMandalaEvaluation() {
      return this.previousMandalaEvaluation !== null
    },
    chartWidth() {
      if (window.innerWidth <= 767) {
        return undefined
      }
      return 400
    }
  },
  watch: {
    async selectedYearMonth() {
      this.isLoading = true
      this.mandalaGoalTemplate = null
      await Promise.all([this.fetchMandalaEvaluations(), this.fetchPreviousMandalaEvaluation()])
      if (this.hasLatestMandalaEvaluation && this.mandalaGoalTemplate?.id !== this.latestMandalaEvaluation.mandalaGoalTemplate) {
        await this.fetchMandalaGoalTemplate(this.latestMandalaEvaluation.mandalaGoalTemplate) 
      }
      this.isLoading = false
    }
  },
  async created() {
    this.isLoading = true
    this.selectedYearMonthDate = this.initialYearMonthDate
    await Promise.all([this.fetchMandalaEvaluations(), this.fetchPreviousMandalaEvaluation()])
    if (this.hasLatestMandalaEvaluation && this.mandalaGoalTemplate?.id !== this.latestMandalaEvaluation.mandalaGoalTemplate) {
      await this.fetchMandalaGoalTemplate(this.latestMandalaEvaluation.mandalaGoalTemplate)
    }
    this.isLoading = false
  },
  methods: {
    generateChartDataFromMandalaEvaluation(mandalaEvaluation, color) {
      const { yearMonth, mandalaGoalSelfEvaluationEntries } = mandalaEvaluation
      const label = format(new Date(yearMonth), 'yyyy年M月')

      const data = mandalaGoalSelfEvaluationEntries.map(entry => entry.totalScore)
      return {
        label,
        backgroundColor: color,
        borderColor: color,
        pointBackgroundColor: color,
        pointRadius: 5,
        pointBorderColor: '#fff',
        data,
      }
    },
    fetchMandalaEvaluations() {
      return this.$axios.get('/mandala_goal_self_evaluations/', {
        params: {
          year_month__in: this.latest3YearMonthStr,
          user__in: this.memberId,
          order_by: '-year_month',
          limit: 4
        }
      }).then(res => {
        const mandalaEvaluations = res.data.results
        // 最新の評価のテンプレートのもののみフィルタリング
        const latestMandalaEvaluation = mandalaEvaluations[0]
        this.mandalaEvaluations = mandalaEvaluations.filter(evaluation => evaluation.mandalaGoalTemplate === latestMandalaEvaluation.mandalaGoalTemplate)
      })
    },
    /**
     * @description 過去の自己評価がないかを確認するためのメソッド
     * 過去の自己評価がない場合は提出期限切れメッセージを出さないようにする
     */
    fetchPreviousMandalaEvaluation() {
      return this.$axios.get('/mandala_goal_self_evaluations/', {
        params: {
          year_month__lte: this.latest3YearMonths[1],
          user__in: this.memberId,
          order_by: '-year_month',
          limit: 1
        }
      }).then(res => {
        this.previousMandalaEvaluation = res.data.results[0] || null
      })
    },
    fetchMandalaGoalTemplate(mandalaGoalTemplateId) {
      return this.$axios.get(`/mandala_goal_templates/${mandalaGoalTemplateId}/`).then(res => {
        this.mandalaGoalTemplate = res.data
      })
    },
    clickMonthButton() {
      this.$router.push({
        path: `/members/${this.memberId}/mandala_evaluation`,
        query: {
          yearMonth: this.selectedYearMonth
        }
      })
    },
    // eslint-disable-next-line no-unused-vars
    onClickChart(event, elements) {
      const { x, y } = event
      // legendのクリック時にはモーダルを開かないように修正
      const isLegendClicked = x <= 100 && y <= 80
      if (!isLegendClicked) {
        this.showMandalaEvaluationEntriesModal = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mandala-section {

  &__template-name {
    color: $grey08;
    font-size: $fs-s;
    font-weight: bold;
  }

  &__empty-message {
    font-size: $fs-m;
    font-weight: bold;
    color: $grey08;
  }
}
</style>